import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

interface EmployerFormState {
  name: string;
  registration_number: string;
  tin: string;
  address: string;
  postal_address: string;
  contact_person: string;
  phone: string;
  email: string;
  industry_sector: string;
  bank_name: string;
  bank_account: string;
  contribution_frequency: string;
  status: string;
  notes: string;
}

const emptyForm: EmployerFormState = {
  name: '',
  registration_number: '',
  tin: '',
  address: '',
  postal_address: '',
  contact_person: '',
  phone: '',
  email: '',
  industry_sector: '',
  bank_name: '',
  bank_account: '',
  contribution_frequency: 'monthly',
  status: 'active',
  notes: '',
};

interface EmployerRecord {
  name: string;
  registration_number: string;
  tin: string | null;
  address: string | null;
  postal_address: string | null;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  industry_sector: string | null;
  bank_name: string | null;
  bank_account: string | null;
  contribution_frequency: string;
  status: string;
  notes: string | null;
}

type FormErrors = Record<string, string[]>;

export function EmployerForm() {
  const { lists, settings } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<EmployerFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get<EmployerRecord>(`/employers/${id}`).then((res) =>
      setForm({
        name: res.data.name,
        registration_number: res.data.registration_number,
        tin: res.data.tin ?? '',
        address: res.data.address ?? '',
        postal_address: res.data.postal_address ?? '',
        contact_person: res.data.contact_person ?? '',
        phone: res.data.phone ?? '',
        email: res.data.email ?? '',
        industry_sector: res.data.industry_sector ?? '',
        bank_name: res.data.bank_name ?? '',
        bank_account: res.data.bank_account ?? '',
        contribution_frequency: res.data.contribution_frequency,
        status: res.data.status,
        notes: res.data.notes ?? '',
      })
    );
  }, [id]);

  const set =
    (key: keyof EmployerFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (id) await api.put(`/employers/${id}`, form);
      else await api.post('/employers', form);
      navigate('/memberships/employers');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>{id ? 'Edit employer' : 'Add employer'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label>
            Name *
            <input value={form.name} onChange={set('name')} required />
            {errors.name && <small className="error">{errors.name[0]}</small>}
          </label>
          <label>
            Registration No. *
            <input value={form.registration_number} onChange={set('registration_number')} required />
            {errors.registration_number && <small className="error">{errors.registration_number[0]}</small>}
          </label>
          <label>
            TIN
            <input value={form.tin} onChange={set('tin')} />
            {errors.tin && <small className="error">{errors.tin[0]}</small>}
          </label>
          <label>
            Industry Sector
            <select value={form.industry_sector} onChange={set('industry_sector')}>
              <option value="">—</option>
              {listOptions(lists, 'industry_sector').map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {errors.industry_sector && <small className="error">{errors.industry_sector[0]}</small>}
          </label>
          <label>
            Address
            <input value={form.address} onChange={set('address')} />
            {errors.address && <small className="error">{errors.address[0]}</small>}
          </label>
          <label>
            Postal Address
            <input value={form.postal_address} onChange={set('postal_address')} />
            {errors.postal_address && <small className="error">{errors.postal_address[0]}</small>}
          </label>
          <label>
            Contact Person
            <input value={form.contact_person} onChange={set('contact_person')} />
            {errors.contact_person && <small className="error">{errors.contact_person[0]}</small>}
          </label>
          <label>
            Phone
            <input
              value={form.phone}
              onChange={set('phone')}
              pattern={settings.mobile_phone_pattern}
              title="Must match the mobile phone format set in System > Settings"
            />
            {errors.phone && <small className="error">{errors.phone[0]}</small>}
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={set('email')} />
            {errors.email && <small className="error">{errors.email[0]}</small>}
          </label>
          <label>
            Bank Name
            <input value={form.bank_name} onChange={set('bank_name')} />
            {errors.bank_name && <small className="error">{errors.bank_name[0]}</small>}
          </label>
          <label>
            Bank Account
            <input value={form.bank_account} onChange={set('bank_account')} />
            {errors.bank_account && <small className="error">{errors.bank_account[0]}</small>}
          </label>
          <label>
            Contribution Frequency *
            <select value={form.contribution_frequency} onChange={set('contribution_frequency')} required>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
            {errors.contribution_frequency && <small className="error">{errors.contribution_frequency[0]}</small>}
          </label>
          <label>
            Status *
            <select value={form.status} onChange={set('status')} required>
              <option value="active">Active</option>
              <option value="dormant">Dormant</option>
              <option value="deregistered">Deregistered</option>
            </select>
            {errors.status && <small className="error">{errors.status[0]}</small>}
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/memberships/employers">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
